import React, {useRef, useState} from 'react';
import * as pdfjs from 'pdfjs-dist';
import {degrees, PDFDocument} from 'pdf-lib';

pdfjs.GlobalWorkerOptions.workerSrc = 'pdf-dist/pdf.worker.min.mjs';

export default function PDFCropper() {
    const [pdf, setPdf] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const canvasRef = useRef(null);
    const mm2Points = 72.0 / 25.4;

    // Hardcoded crop constants - you can adjust these as needed
    const cropConstants = {
        x: 480, // X position to start crop (points)
        y: 0, // Y position to start crop (points)
        width: 307, // Width of crop area (points)
        height: 595, // Height of crop area (points)
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file || file.type !== 'application/pdf') {
            setError('Please upload a valid PDF file.');
            return;
        }
        setIsLoading(true);
        setError(null);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfData = new Uint8Array(arrayBuffer);

            // Load PDF with PDF.js
            const loadingTask = pdfjs.getDocument({data: new Uint8Array(pdfData)});
            const pdfDoc = await loadingTask.promise;

            if (pdfDoc.numPages < 1) {
                throw new Error('PDF has no pages');
            }
            setPdf({
                document: pdfDoc,
                data: pdfData
            });

            // Render first page preview
            await renderPage(pdfDoc);
        } catch (err) {
            console.error('Error loading PDF:', err);
            setError('Failed to load PDF: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const renderPage = async (pdfDoc) => {
        try {
            // Get the first page
            const page = await pdfDoc.getPage(pdfDoc.numPages);

            // Get viewport
            const viewport = page.getViewport({scale: 1.0, rotation: 90});

            // Prepare canvas
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            // Set canvas dimensions to match page
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            // Render PDF page to canvas
            await page.render({
                canvasContext: context,
                viewport: viewport
            }).promise;

            // Draw crop rectangle (for visual reference)
            context.strokeStyle = 'red';
            context.lineWidth = 2;
            context.strokeRect(
                cropConstants.x,
                viewport.height - cropConstants.y - cropConstants.height, // PDF coordinates are from bottom left
                cropConstants.width,
                cropConstants.height
            );

        } catch (err) {
            console.error('Error rendering page:', err);
            setError('Failed to render PDF page');
        }
    };

    const cropAndPrintPDF = async () => {

        if (!pdf) return;

        setIsLoading(true);

        try {
            const sourcePdfDoc = await PDFDocument.load(pdf.data)
            const page = sourcePdfDoc.getPage(sourcePdfDoc.getPageCount() - 1)

            // Create a new PDF with just the cropped area
            const newPdfDoc = await PDFDocument.create();
            const newPage = newPdfDoc.addPage([199 * mm2Points, 103 * mm2Points]);

            // noinspection JSSuspiciousNameCombination
            const embeddedPage = await newPdfDoc.embedPage(page, {
                left: cropConstants.y,
                bottom: cropConstants.x,
                right: cropConstants.y + cropConstants.height,
                top: cropConstants.x + cropConstants.width,
            });

            // Draw the cropped portion on the new page
            newPage.drawPage(embeddedPage, {
                x: newPage.getWidth(),
                y: newPage.getHeight(),
                width: newPage.getWidth(),
                height: newPage.getHeight(),
                rotate: degrees(180)
            });

            // Save the new PDF
            const newPdfBytes = await newPdfDoc.save();

            // Create a blob URL for the new PDF
            const blob = new Blob([newPdfBytes], {type: 'application/pdf'});
            const url = URL.createObjectURL(blob);

            const setPrint = function () {
                const closePrint = () => {
                    document.body.removeChild(this);
                };
                this.contentWindow.onbeforeunload = closePrint;
                this.contentWindow.onafterprint = closePrint;
                this.contentWindow.print();
            }

            const hideFrame = document.createElement("iframe");
            hideFrame.onload = setPrint;
            hideFrame.style.display = "none"; // hide iframe
            hideFrame.src = url;
            document.body.appendChild(hideFrame);

            // Open print dialog

        } catch (err) {
            console.error('Error cropping PDF:', err);
            setError('Failed to crop and print PDF: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">DHL Paketmarken zu Etikettendrucker Tool</h1>

            <div className="w-full mb-6">
                <label className="block mb-2 font-medium">
                    DHL Paketmarke laden (keine Übertragung zu einem Server):
                    <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileChange}
                        className="block w-full mt-1 p-2 border border-gray-300 rounded"
                        disabled={isLoading}
                    />
                </label>
            </div>

            {error && (
                <div className="w-full p-4 mb-4 bg-red-100 text-red-700 rounded">
                    {error}
                </div>
            )}

            {pdf && (
                <div className="w-full flex flex-col items-center h-fit">
                    <div className="mb-4 p-4 border border-gray-300 rounded overflow-auto">
                        <p className="mb-2 font-medium">Vorschau (103mm x 199mm Etikett):</p>
                        <canvas ref={canvasRef} className="border border-gray-200" onClick={cropAndPrintPDF}
                        />
                    </div>
                    <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            onClick={cropAndPrintPDF}>Drucken
                    </button>

                </div>
            )}


            <footer className="rounded-lg">
                <div className="w-full mx-auto max-w-screen-xl p-4">
      <span className="text-sm  sm:text-center">© 2025 <a href=" https://ideas-in-logic.de/"
                                                          className="underline">Wolfgang Jung</a>. All Rights Reserved.
                </span>
                </div>
            </footer>


        </div>

    )
        ;
}
